import BooksSvg from "@icons/books.svg?react";
import ChartSvg from "@icons/chart.svg?react";
import GarageOpenSvg from "@icons/garage-open.svg?react";
import MagnifyChartSvg from "@icons/magnify-chart.svg?react";
import MarketPlaceSvg from "@icons/marketplace.svg?react";
import NewspaperSvg from "@icons/newspaper.svg?react";
import SeedlingSvg from "@icons/seedling.svg?react";
import SendMoneySvg from "@icons/send-money.svg?react";
import UsdCircleSvg from "@icons/usd-circle.svg?react";
import { t } from "@lingui/core/macro";
import { NavLink } from "react-router-dom";

import { Features } from "@/common/providers/feature-provider";
import { PlanSchema } from "@/common/services/user";
import { MenuItemFlow } from "@/domain/plan/nav-bar/nav-bar.flow";
import { FeatureToType } from "@/generated/api/users";
import { path as purchasePricesPath } from "@/routes/purchase-prices/purchase-prices";
import { path as reportsPath } from "@/routes/reports/reports";
import { getAbsolutePath } from "@/routes/routes.utils";
import { path as stockExchangePath } from "@/routes/stock-exchange/stock-exchange";
import { path as storagesPath } from "@/routes/storages/storages";
import { path as tradesPath } from "@/routes/trades/trades";
import { path as yieldPredictionsPath } from "@/routes/yield-predictions/yield-predictions";

import { getClassName } from "./nav-bar.utils";

type Link = {
  className?: string | ((args: { isActive: boolean }) => string);
  to: string;
  icon: React.ReactNode;
  label: string;
};

type LinkWithFeature = Link & { feature: FeatureToType };

const NavBar = ({
  plan,
  renderItem,
}: {
  plan: Omit<PlanSchema, "planId"> & {
    features: Features;
    planId: PlanSchema["planId"] | null;
  };
  renderItem?: (
    link: Link,
    wrapper: Exclude<
      ReturnType<InstanceType<typeof MenuItemFlow>["getWrapper"]>,
      null
    >,
  ) => React.ReactNode;
}) => {
  const links: LinkWithFeature[] = [
    {
      className: getClassName,
      feature: FeatureToType.PURCHASE_PRICES,
      icon: <MagnifyChartSvg />,
      label: t`Moje výkupní ceny`,
      to: getAbsolutePath(purchasePricesPath),
    },
    {
      className: getClassName,
      feature: FeatureToType.QUICK_TRADING,
      icon: <SendMoneySvg />,
      label: t`Rychlý prodej komodit`,
      to: getAbsolutePath("quick-trading"),
    },
    {
      className: getClassName,
      feature: FeatureToType.MARKETPLACE,
      icon: <MarketPlaceSvg />,
      label: t`Nákup materiálu`,
      to: getAbsolutePath("marketplace"),
    },
    {
      className: getClassName,
      feature: FeatureToType.YIELD_PREDICTIONS,
      icon: <SeedlingSvg />,
      label: t`Predikce výnosů`,
      to: getAbsolutePath(yieldPredictionsPath),
    },
    {
      className: getClassName,
      feature: FeatureToType.STOCK_EXCHANGE,
      icon: <ChartSvg />,
      label: t`Burzovní informace`,
      to: getAbsolutePath(stockExchangePath),
    },
    {
      className: getClassName,
      feature: FeatureToType.MARKET_NEWS,
      icon: <NewspaperSvg />,
      label: t`Analýza trhu`,
      to: getAbsolutePath(reportsPath),
    },
    {
      className: getClassName,
      feature: FeatureToType.TRADING,
      icon: <UsdCircleSvg />,
      label: t`Prodej komodit`,
      to: getAbsolutePath(tradesPath),
    },
    {
      className: getClassName,
      feature: FeatureToType.STORAGES,
      icon: <GarageOpenSvg />,
      label: t`Sklady`,
      to: getAbsolutePath(storagesPath),
    },
    {
      className: getClassName,
      feature: FeatureToType.TRANSACTIONS_ORDERS,
      icon: <BooksSvg />,
      label: t`Transakce a příkazy`,
      to: getAbsolutePath("transactions-and-orders"),
    },
  ];

  return (
    <nav className="shadow-can-light-box flex w-full grow flex-col items-center gap-3 font-bold text-white">
      {links.map((link) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { feature, ...restOfLink } = link;
        const flow = new MenuItemFlow(link, plan);
        const Wrapper = flow.getWrapper();

        if (!Wrapper) {
          return null;
        }

        return typeof renderItem === "function" ? (
          renderItem(restOfLink, Wrapper)
        ) : (
          <NavLink
            className={link.className}
            key={link.to}
            title={link.label}
            to={link.to}
            viewTransition
          >
            <Wrapper>{link.icon}</Wrapper>
          </NavLink>
        );
      })}
    </nav>
  );
};

export { NavBar };
export type { LinkWithFeature };
